import { useEffect, useState } from "react";
import { notifyError } from "../utils/toast";
import { handleGetRoleBasedUsers } from "../services/userService";
import { handleGetAllTasks } from "../services/routes/taskRoute";

const useTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [taskCache, setTaskCache] = useState(new Map());
  const [presonList, setPresonList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [nextId, setNextId] = useState("");
  const [prevId, setPrevId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const fetchPage = async (cursor = "", direction = "next", pageNumber = 1) => {
    setLoading(true);
    try {
      const options = {
        limitCount: limit,
        startAfterDoc: direction === "next" ? cursor : "",
        assigneeId: filters?.assistant?.value || "",
        startDate: formatDate(filters?.startDate),
        endDate: formatDate(filters?.endDate),
        status: filters?.status?.value || "",
        initialInfoMedium: filters?.initialInfoMedium?.value || "",
        finalInfoMedium: filters?.finalInfoMedium?.value || "",
        active: typeof filters?.activeStatus?.value === "boolean" ? filters.activeStatus.value : true,
      };

      const response = await handleGetAllTasks(options);
      const taskList = response?.tasks || [];

      const receivedFullPage = taskList.length === limit;
      const hasMoreData = receivedFullPage && !!response.lastDoc;

      setTasks(taskList);
      setNextId(response?.lastDoc || "");
      setPrevId(response?.firstDoc || "");
      setHasMore(hasMoreData);

      setTaskCache((prev) => new Map(prev).set(pageNumber, taskList));
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInitial = () => {
    setTaskCache(new Map());
    setCurrentPage(1);
    setNextId("");
    setPrevId("");
    fetchPage("", "next", 1);
  };

  const handlePageChange = (cursor, direction) => {
    if (direction === "next") {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      if (taskCache.has(nextPage)) {
        setTasks(taskCache.get(nextPage));
      } else {
        fetchPage(nextId, "next", nextPage);
      }
    } else if (direction === "previous" && currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);

      if (taskCache.has(prevPage)) {
        setTasks(taskCache.get(prevPage));
      } else {
        fetchPage(cursor, "previous", prevPage);
      }
    }
  };

  const handlePageSize = (value) => {
    setLimit(value);
    setCurrentPage(1);
    setTaskCache(new Map());
    setNextId("");
    fetchPage("", "next", 1);
  };

  const handleApplyFilters = (newFilters) => {
    const { startDate, endDate } = newFilters;

    if ((startDate && !endDate) || (!startDate && endDate)) {
      notifyError("Please select both Start Date and End Date.");
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      notifyError("Start Date cannot be after End Date.");
      return;
    }

    setFilters(newFilters);
    setModalOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const removeFilter = (filterKey) => {
    const updatedFilters = { ...filters };

    if (filterKey === "startDate" || filterKey === "endDate") {
      delete updatedFilters.startDate;
      delete updatedFilters.endDate;
    } else {
      delete updatedFilters[filterKey];
    }

    setFilters(updatedFilters);
  };

  const handlePresonList = async () => {
    try {
      const response = await handleGetRoleBasedUsers("");
      setPresonList(response);
    } catch (error) {
      console.error("Error fetching person list:", error);
      notifyError("Failed to fetch person list. Please try again.");
    }
  };

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, limit]);

  useEffect(() => {
    handlePresonList();
  }, []);

  return {
    tasks,
    loading,
    limit,
    currentPage,
    nextId,
    prevId,
    hasMore,
    handlePageChange,
    handlePageSize,

    filters,
    handleApplyFilters,
    handleClearFilters,
    removeFilter,
    modalOpen,
    setModalOpen,

    presonList,
  };
};

export default useTasks;
